import { useAccount } from '@azure/msal-react';
import { DEV_HEALTH_REFRESH_MS, useDevHealth, type ProbeStatus } from '../hooks/useDevHealth';

const refreshSeconds = DEV_HEALTH_REFRESH_MS / 1000;

const statusLabel: Record<ProbeStatus, string> = {
  loading: 'Checking…',
  ok: 'OK',
  degraded: 'Degraded',
  error: 'Unavailable',
};

const statusColour: Record<ProbeStatus, string> = {
  loading: '#6b7280',
  ok: '#15803d',
  degraded: '#b45309',
  error: '#b91c1c',
};

function StatusBadge({ status }: { status: ProbeStatus }) {
  return (
    <span
      style={{
        color: statusColour[status],
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {statusLabel[status]}
    </span>
  );
}

/**
 * Development-only system health summary. Hidden in production builds.
 * Probes the local API via the Vite dev proxy (`/api` → API_PORT).
 */
export function DevHealthPanel() {
  const account = useAccount();
  const health = useDevHealth(import.meta.env.DEV);
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'not set';

  if (!import.meta.env.DEV) {
    return null;
  }

  return (
    <section
      aria-labelledby="dev-health-heading"
      style={{
        marginTop: '2rem',
        padding: '1rem 1.25rem',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        background: '#f9fafb',
        fontSize: '0.9rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: '1rem',
          marginBottom: '0.75rem',
        }}
      >
        <h2 id="dev-health-heading" style={{ margin: 0, fontSize: '1rem' }}>
          Development — system health
        </h2>
        <button
          type="button"
          onClick={health.refresh}
          style={{ fontSize: '0.85rem', cursor: 'pointer' }}
        >
          Refresh
        </button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <th scope="row" style={{ textAlign: 'left', padding: '0.35rem 0.5rem 0.35rem 0', fontWeight: 500 }}>
              API liveness
            </th>
            <td style={{ padding: '0.35rem 0' }}>
              <code>/healthz</code> — <StatusBadge status={health.apiLive} />
            </td>
          </tr>
          <tr>
            <th scope="row" style={{ textAlign: 'left', padding: '0.35rem 0.5rem 0.35rem 0', fontWeight: 500 }}>
              PostgreSQL
            </th>
            <td style={{ padding: '0.35rem 0' }}>
              <code>/readyz</code> — <StatusBadge status={health.dbReady} />
            </td>
          </tr>
          <tr>
            <th scope="row" style={{ textAlign: 'left', padding: '0.35rem 0.5rem 0.35rem 0', fontWeight: 500 }}>
              API base URL
            </th>
            <td style={{ padding: '0.35rem 0' }}>
              <code>{apiBaseUrl}</code>
            </td>
          </tr>
          {account ? (
            <tr>
              <th scope="row" style={{ textAlign: 'left', padding: '0.35rem 0.5rem 0.35rem 0', fontWeight: 500 }}>
                Signed in as
              </th>
              <td style={{ padding: '0.35rem 0' }}>{account.username}</td>
            </tr>
          ) : null}
          <tr>
            <th scope="row" style={{ textAlign: 'left', padding: '0.35rem 0.5rem 0.35rem 0', fontWeight: 500 }}>
              Last checked
            </th>
            <td style={{ padding: '0.35rem 0' }}>
              {health.lastChecked ? health.lastChecked.toLocaleTimeString() : '—'}
              <span style={{ color: '#6b7280' }}> (auto-refresh every {refreshSeconds}s)</span>
            </td>
          </tr>
        </tbody>
      </table>

      {health.detail ? (
        <p style={{ margin: '0.75rem 0 0', color: '#b91c1c' }} role="status">
          {health.detail}
        </p>
      ) : null}

      <p style={{ margin: '0.75rem 0 0', color: '#6b7280', fontSize: '0.8rem' }}>
        Run <code>npm run dev:api</code> in a separate terminal if probes show unavailable.
      </p>
    </section>
  );
}
